import type { SupabaseClient } from '@supabase/supabase-js';
import { createStore, type Store } from '../core/store';

/**
 * Account sessions.
 *
 * A thin, defensive layer over Supabase Auth: one observable state, forms that
 * receive plain-language results, and no raw server text shown to the person.
 * Sessions persist in the browser's localStorage and refresh themselves
 * (supabase-js `persistSession` / `autoRefreshToken`); the PKCE flow is used so
 * confirmation and recovery links carry a one-time code, never tokens.
 */

export interface AuthUser {
  id: string;
  email: string;
}

export type AuthStatus =
  /** Cloud is not configured in this build: MyTXT is a local editor. */
  | 'off'
  | 'loading'
  | 'signed-out'
  | 'signed-in'
  /** Arrived through a recovery link: a new password must be chosen. */
  | 'recovery';

export interface AuthNotice {
  tone: 'info' | 'error';
  text: string;
}

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** The session ended without the person asking (revoked, refresh failed, signed out elsewhere). */
  expired: boolean;
  notice: AuthNotice | null;
}

export interface AuthResult {
  ok: boolean;
  message: string;
}

/** `offline` means "could not tell right now" — unreachable, throttled or failing; the session may well be fine. */
export type Revalidation = 'ok' | 'gone' | 'offline';

export interface AuthService {
  readonly state: Store<AuthState>;
  /** Restore the stored session and handle a confirmation / recovery link in the URL. */
  start(): Promise<void>;
  signIn(email: string, password: string): Promise<AuthResult>;
  signUp(email: string, password: string): Promise<AuthResult>;
  requestReset(email: string): Promise<AuthResult>;
  setPassword(password: string): Promise<AuthResult>;
  resendConfirmation(email: string): Promise<AuthResult>;
  /** Deliberate sign-out. */
  signOut(): Promise<AuthResult>;
  /** Check (and if needed refresh) the session after a request was refused. */
  revalidate(): Promise<Revalidation>;
  dismissNotice(): void;
}

export const MIN_PASSWORD = 10;

const NETWORK = 'MyTXT can’t reach the cloud right now. Check your connection and try again.';
const GENERIC = 'Something went wrong. Please try again.';

interface AuthLikeError {
  code?: string;
  status?: number;
  message?: string;
  name?: string;
}

export function isNetworkFailure(error: AuthLikeError | null | undefined): boolean {
  if (!error) return false;
  return error.name === 'AuthRetryableFetchError' || error.status === 0 || /failed to fetch|networkerror|load failed/i.test(error.message ?? '');
}

/** Plain-language text for an Auth failure. Raw server messages are not shown, except our own sign-up gate text. */
export function describeAuthError(error: AuthLikeError | null | undefined, context: 'sign-in' | 'sign-up' | 'reset' | 'password' | 'resend' = 'sign-in'): string {
  if (!error) return GENERIC;
  if (isNetworkFailure(error)) return NETWORK;
  const code = error.code ?? '';
  if (/private beta/i.test(error.message ?? '')) return 'MyTXT is in a private beta. This email address has not been invited.';
  switch (code) {
    case 'invalid_credentials':
      return 'That email and password don’t match.';
    case 'email_not_confirmed':
      return 'Confirm your email address first — look for the message from MyTXT.';
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
    case 'over_sms_send_rate_limit':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'signup_disabled':
    case 'email_provider_disabled':
      return 'Registration is closed. MyTXT is in a private beta.';
    case 'weak_password':
      return `Choose a stronger password (at least ${MIN_PASSWORD} characters).`;
    case 'same_password':
      return 'Choose a password you haven’t used for this account before.';
    case 'otp_expired':
    case 'flow_state_expired':
    case 'flow_state_not_found':
    case 'bad_code_verifier':
      return 'That link has expired or was already used. Request a new one.';
    case 'user_banned':
      return 'This account has been disabled.';
    case 'session_expired':
    case 'session_not_found':
    case 'refresh_token_not_found':
    case 'refresh_token_already_used':
      return 'Your session ended. Sign in again.';
  }
  if (error.status === 429) return 'Too many attempts. Wait a few minutes and try again.';
  if (context === 'sign-up' && (error.status === 403 || error.status === 422 || error.status === 500)) return 'Registration isn’t available for this email address. MyTXT is in a private beta.';
  return GENERIC;
}

const SESSION_LOSS_CODES = new Set(['refresh_token_not_found', 'refresh_token_already_used', 'session_not_found', 'session_expired', 'bad_jwt', 'user_not_found', 'user_banned']);

/** The server has said, definitively, that this session is over (not merely that it is busy or unreachable). */
export function isSessionLoss(error: AuthLikeError | null | undefined): boolean {
  if (!error || isNetworkFailure(error)) return false;
  if (error.name === 'AuthSessionMissingError') return true;
  if (error.code && SESSION_LOSS_CODES.has(error.code)) return true;
  return error.status === 400 || error.status === 401 || error.status === 403;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const validEmail = (email: string) => EMAIL.test(email.trim());

/** Auth parameters a redirect may leave in the query string or the hash. */
function redirectParams(location: Pick<Location, 'search' | 'hash'>): URLSearchParams {
  const params = new URLSearchParams(location.search);
  for (const [k, v] of new URLSearchParams(location.hash.replace(/^#/, ''))) if (!params.has(k)) params.set(k, v);
  return params;
}

export function noticeForRedirect(params: URLSearchParams, initError: AuthLikeError | null): AuthNotice | null {
  const description = params.get('error_description') ?? '';
  const code = params.get('error_code') ?? initError?.code ?? '';
  if (params.has('error') || params.has('error_description') || code) {
    return { tone: 'error', text: describeAuthError({ code, message: description }) };
  }
  if (initError) {
    if (initError.name === 'AuthPKCECodeVerifierMissingError' || /verifier/i.test(initError.message ?? '')) {
      return { tone: 'info', text: 'If you just confirmed your email, sign in to continue. (Open links in the same browser where you started.)' };
    }
    return { tone: 'error', text: describeAuthError(initError) };
  }
  return null;
}

function toUser(user: { id: string; email?: string | null } | null | undefined): AuthUser | null {
  return user ? { id: user.id, email: user.email ?? '' } : null;
}

export function createAuthService(client: SupabaseClient, env: { location?: Location; history?: History } = {}): AuthService {
  const auth = client.auth;
  const location = env.location ?? globalThis.location;
  const history = env.history ?? globalThis.history;
  const state = createStore<AuthState>({ status: 'loading', user: null, expired: false, notice: null });
  let deliberate = false;
  let started = false;

  const set = (patch: Partial<AuthState>) => state.set((s) => ({ ...s, ...patch }));

  const redirectTo = () => `${location.origin}${location.pathname}`;

  auth.onAuthStateChange((event, session) => {
    // Never await Supabase calls in here; only record what happened.
    switch (event) {
      case 'INITIAL_SESSION':
        if (session) set({ status: state.get().status === 'recovery' ? 'recovery' : 'signed-in', user: toUser(session.user), expired: false });
        else if (state.get().status === 'loading') set({ status: 'signed-out', user: null });
        break;
      case 'SIGNED_IN':
        set({ status: state.get().status === 'recovery' ? 'recovery' : 'signed-in', user: toUser(session?.user), expired: false });
        break;
      case 'PASSWORD_RECOVERY':
        set({ status: 'recovery', user: toUser(session?.user), expired: false });
        break;
      case 'TOKEN_REFRESHED':
      case 'USER_UPDATED':
        if (session) set({ user: toUser(session.user) });
        break;
      case 'SIGNED_OUT':
        set({ status: 'signed-out', expired: !deliberate && state.get().status !== 'signed-out', user: deliberate ? null : state.get().user });
        deliberate = false;
        break;
    }
  });

  return {
    state,

    async start() {
      if (started) return;
      started = true;
      const params = redirectParams(location);
      const { error } = await auth.initialize();
      const notice = noticeForRedirect(params, error as AuthLikeError | null);
      if (notice || params.has('code') || params.has('error')) {
        // Never leave one-time codes or error details in the address bar.
        try {
          history.replaceState(history.state, '', `${location.origin}${location.pathname}`);
        } catch {
          // A history we cannot rewrite is harmless.
        }
      }
      if (state.get().status === 'loading') {
        const { data } = await auth.getSession();
        set({ status: data.session ? 'signed-in' : 'signed-out', user: toUser(data.session?.user) });
      }
      if (notice) set({ notice });
      else if (params.has('code') && state.get().status === 'signed-out') {
        // A one-time code arrived but there was no flow here to complete (the link was opened in another browser).
        set({ notice: { tone: 'info', text: 'If you just confirmed your email, sign in to continue. (Open links in the same browser where you started.)' } });
      }
    },

    async signIn(email, password) {
      if (!validEmail(email)) return { ok: false, message: 'Enter a valid email address.' };
      if (!password) return { ok: false, message: 'Enter your password.' };
      try {
        const { error } = await auth.signInWithPassword({ email: email.trim(), password });
        if (error) return { ok: false, message: describeAuthError(error, 'sign-in') };
        return { ok: true, message: '' };
      } catch {
        return { ok: false, message: NETWORK };
      }
    },

    async signUp(email, password) {
      if (!validEmail(email)) return { ok: false, message: 'Enter a valid email address.' };
      if (password.length < MIN_PASSWORD) return { ok: false, message: `Choose a password of at least ${MIN_PASSWORD} characters.` };
      try {
        const { error } = await auth.signUp({ email: email.trim(), password, options: { emailRedirectTo: redirectTo() } });
        if (error) return { ok: false, message: describeAuthError(error, 'sign-up') };
        // The same answer whether or not the address is new: registration must not reveal who has an account.
        return { ok: true, message: 'Check your inbox to confirm your email address, then sign in.' };
      } catch {
        return { ok: false, message: NETWORK };
      }
    },

    async requestReset(email) {
      if (!validEmail(email)) return { ok: false, message: 'Enter a valid email address.' };
      try {
        const { error } = await auth.resetPasswordForEmail(email.trim(), { redirectTo: redirectTo() });
        if (error && (isNetworkFailure(error) || error.status === 429 || /rate/.test(error.code ?? ''))) return { ok: false, message: describeAuthError(error, 'reset') };
        return { ok: true, message: 'If an account exists for that address, a link to choose a new password is on its way.' };
      } catch {
        return { ok: false, message: NETWORK };
      }
    },

    async setPassword(password) {
      if (password.length < MIN_PASSWORD) return { ok: false, message: `Choose a password of at least ${MIN_PASSWORD} characters.` };
      try {
        const { error } = await auth.updateUser({ password });
        if (error) return { ok: false, message: describeAuthError(error, 'password') };
        if (state.get().status === 'recovery') set({ status: 'signed-in' });
        return { ok: true, message: 'Your password has been changed.' };
      } catch {
        return { ok: false, message: NETWORK };
      }
    },

    async resendConfirmation(email) {
      if (!validEmail(email)) return { ok: false, message: 'Enter a valid email address.' };
      try {
        const { error } = await auth.resend({ type: 'signup', email: email.trim(), options: { emailRedirectTo: redirectTo() } });
        if (error && (isNetworkFailure(error) || error.status === 429 || /rate/.test(error.code ?? ''))) return { ok: false, message: describeAuthError(error, 'resend') };
        return { ok: true, message: 'If that address is waiting for confirmation, a new message is on its way.' };
      } catch {
        return { ok: false, message: NETWORK };
      }
    },

    async signOut() {
      deliberate = true;
      try {
        // Local scope: this device only. Other devices stay signed in.
        const { error } = await auth.signOut({ scope: 'local' });
        if (error && !isNetworkFailure(error)) {
          deliberate = false;
          return { ok: false, message: GENERIC };
        }
      } catch {
        // Offline sign-out still ends the local session below.
      }
      // supabase-js clears local storage even when the server cannot be reached; make the state agree.
      set({ status: 'signed-out', user: null, expired: false });
      deliberate = false;
      return { ok: true, message: '' };
    },

    async revalidate() {
      try {
        const { data, error } = await auth.refreshSession();
        if (data.session) return 'ok';
        if (!isSessionLoss(error as AuthLikeError | null)) return 'offline';
        set({ status: 'signed-out', expired: true });
        return 'gone';
      } catch {
        return 'offline';
      }
    },

    dismissNotice() {
      set({ notice: null });
    },
  };
}

/** The service for a build without cloud configuration: everything stays local. */
export function createOffAuth(): AuthService {
  const state = createStore<AuthState>({ status: 'off', user: null, expired: false, notice: null });
  const closed = async (): Promise<AuthResult> => ({ ok: false, message: 'Cloud accounts are not set up in this copy of MyTXT.' });
  return {
    state,
    start: async () => {},
    signIn: closed,
    signUp: closed,
    requestReset: closed,
    setPassword: closed,
    resendConfirmation: closed,
    signOut: closed,
    revalidate: async () => 'gone',
    dismissNotice: () => {},
  };
}
